import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuestionnaire } from 'hooks/useQuestionnaire';
import { useTabContext } from 'context/TabContext';
import { Box, Button } from '@chakra-ui/react';
import Heading from 'components/Heading/Heading';
import Checkbox from 'components/Checkbox/Checkbox';
import SectionComponent from 'components/SectionComponent/SectionComponent';
import { useLoading } from 'hooks/useLoading';
import { useSession } from 'context/SessionContext';
import { Section } from 'types';
import { ExtraAnswer, useExtraQuestionsData } from 'context/ExtraQuestionsContext';
import { initializeDefaultValues, resetFormValues } from 'utils/formUtils';
import { fetchQuestions as handleFetchQuestions } from 'hooks/useFetchQuestions';

const Professional: React.FC = () => {
  const currentTab = 4;
  const { completeTab, activeTab, loading, setLoading, resolveEmptyTab, enabledTabs, setEmptyTabs } = useTabContext();
  const { startLoading, stopLoading, LoadingSpinner } = useLoading();
  const [professionalData, setProfessionalData] = useState<Section[]>([]);
  const { saveQuestionnaire } = useSession();
  const { extraAnswers } = useExtraQuestionsData();
  const { validateStep, validateInputs, validateSubQuestion, editValues } =
    useQuestionnaire();

  const filteredProfession: ExtraAnswer[] = useMemo(() => (
    extraAnswers.filter(item => item.activity === 'P')
  ), [extraAnswers]);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    reset,
    setValue,
  } = useForm();

  useEffect(() => {
    if (activeTab === currentTab) {
      if (!filteredProfession.length) {
        resolveEmptyTab(activeTab);
        return;
      };

      // remove the current tab from the empty tabs list
      setEmptyTabs(prev => prev.filter(tab => tab !== activeTab));

      const fetchQuestionsProfessional = async () => {
        startLoading();

        try {
          const { code, activity } = filteredProfession[0];

         const { sections } = await handleFetchQuestions(code, activity);

          if (!sections[0].items.length) {
            resolveEmptyTab(activeTab);
            return;
          }

          setProfessionalData(sections);
          
        } catch (error: any) {
          completeTab(activeTab);
        } finally {
          stopLoading();
        }
      };

      if(professionalData.length === 0) fetchQuestionsProfessional();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!enabledTabs.includes(currentTab)) {
      resetFormValues(professionalData, setValue, reset, initializeDefaultValues(professionalData));
    }
  }, [enabledTabs]);

  const handleNextPage = () => {
    setLoading(true);

    const valuesProfessional = getValues();
    validateStep(valuesProfessional);
    const answersProfessional = editValues(getValues);
    
    const { code, activity } = filteredProfession[0];

    const answersProfessionalData = {
      questionnaireId: code,
      activityCode: activity,
      answers: [...answersProfessional],
    };

    saveQuestionnaire(answersProfessionalData);

    try {
      completeTab(activeTab);
    } catch (error) {
      console.error('Erro ao enviar os dados do questionário:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box>
        <Heading title="Profissional" />
      </Box>
      <Box>
        <form onSubmit={handleSubmit(handleNextPage)}>
          {professionalData.map(sectionList => (
            <SectionComponent
              key="professionalPage"
              sectionList={sectionList}
              control={control}
              validateInputs={validateInputs}
              getValues={getValues}
              validateSubQuestion={validateSubQuestion}
              errors={errors}
              handleDataChange={setProfessionalData}
              dataListChange={professionalData}
            />
          ))}

          <Checkbox
            name="confirmInfoProfissional"
            control={control}
            label="Confirmo a Informação"
            rules={{ required: 'Campo de preenchimento obrigatório' }}
            error={errors?.['confirmInfoProfissional']}
          />

          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <Button
              variant={'primary'}
              type="submit"
              isLoading={loading}
              loadingText="A Enviar"
            >
              <span>Continuar</span>
            </Button>
          </Box>
        </form>
      </Box>
      <LoadingSpinner />
    </Box>
  );
};

export default Professional;
